import React from 'react';
import * as R from 'ramda';
import { useIntl } from '@atlassian/jira-intl';
import { Tooltip } from '@atlassian/jira-portfolio-3-common';
import type {
	OptionType,
	OptionsGroup,
	FormatOptionLabelMeta,
} from '@atlassian/jira-portfolio-3-common/src/select/types';
import type { IssueStatusesById } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issue-statuses/types';
import type { ScopeIssue } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/scope/types';
import type { Sprint } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/sprints';
import { getFutureSprintGroups } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/sprints/utils';
// delete this reference in JPO-19568 and copy the parent message to the current component
import messages from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/fields/columns/cells/sprint/messages';
import { getSprintName } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/fields/columns/cells/sprint/utils';
import Cell from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/fields/columns/column/cell/view';
import {
	getOptimizedValue,
	hasValueChanged,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/util';
import { withSlots, slots } from '@atlassian/jira-portfolio-3-portfolio/src/common/component-slots';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import { SPRINT_STATES } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import { MIN_SELECT_WIDTH } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/select';

import { DisplayLabel, MenuLabel } from './option-label';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import { type Props, issueAttributeName } from './types';

const sprintOfIssueIsCompleted = (issue: ScopeIssue): boolean =>
	isDefined(issue.sprint) &&
	Array.isArray(issue.completedSprints) &&
	R.contains(issue.sprint, issue.completedSprints);

const getOptionLabel = ({
	issue,
	issueStatuses,
	sprints,
	showOptimizations,
}: {
	issue: ScopeIssue;
	issueStatuses: IssueStatusesById;
	sprints: Sprint[];
	showOptimizations: boolean | undefined;
}) => {
	const issueIsDone = Boolean(
		issueStatuses &&
			issue.status &&
			issueStatuses[issue.status] &&
			issueStatuses[issue.status].categoryId === 3,
	);

	const issueIsCompleted = issueIsDone && sprintOfIssueIsCompleted(issue);

	const completedSprintIds = issue.completedSprints || [];

	const completedSprints = R.pipe<Sprint[], Sprint[], Sprint[], Sprint[]>(
		R.filter((sprint: Sprint) => R.includes(sprint.id, completedSprintIds)),
		R.sortBy(R.prop('completeDate')),
		R.reverse,
	)(sprints);

	const sprintIsOptimized =
		!!showOptimizations &&
		hasValueChanged(issue.sprint, getOptimizedValue(issue, issueAttributeName));

	return (opt: OptionType, { context }: FormatOptionLabelMeta) => {
		const sprintNotSet = !isDefined(issue.sprint);

		if (context === 'menu') {
			const sprintIsCompleted = opt.isDisabled;

			return (
				<MenuLabel sprintIsExternal={opt.isExternal} sprintIsCompleted={sprintIsCompleted}>
					{opt.label}
				</MenuLabel>
			);
		}

		return (
			<DisplayLabel
				issueIsCompleted={issueIsCompleted}
				issueCompletedSprints={completedSprints}
				sprintIsOptimized={sprintIsOptimized}
				sprintIsExternal={opt.isExternal}
				sprintNotSet={sprintNotSet}
			>
				{opt.label}
			</DisplayLabel>
		);
	};
};

export const SprintSelect = (props: Props) => {
	const {
		handleSelection,
		isScrolling,
		isReadOnly,
		issue,
		issue: { sprint: sprintOfIssue, team: teamOfIssue },
		showOptimizations,
		sprints,
		message,
		issueStatuses,
		sprintsByTeam,
		teams,
		sprintsByIssueSources,
		additionalTeamsById,
		teamsById,
		allSprints,
		externalSprintsById,
		externalSprintLabel,
		sprintRollUpForIssue,
		SelectForColumnCell,
	} = props;

	const completedSprints = issue.completedSprints || [];
	const completedSprintsForIssue = allSprints.filter(
		(sprint) => sprint.state === SPRINT_STATES.CLOSED && R.contains(sprint.id, completedSprints),
	);

	const { formatMessage } = useIntl();
	const otherSprintsLabel = formatMessage(messages.otherSprints);

	const futureSprintsGroupByTeam = getFutureSprintGroups(
		teamOfIssue,
		sprintOfIssue,
		sprintsByIssueSources,
		sprintsByTeam,
		additionalTeamsById,
		teams,
		teamsById,
		otherSprintsLabel,
	);

	const completedSprintOptions = completedSprintsForIssue.map((sprint) => ({
		value: sprint.id,
		label: sprint.title,
		isDisabled: true,
		isExternal: isDefined(externalSprintsById[sprint.id]),
	}));

	const groupedOptions: OptionsGroup[] = [
		{
			label: formatMessage(messages.completedSprintsHeader),
			options: completedSprintOptions,
		},
	];

	// eslint-disable-next-line @typescript-eslint/no-shadow
	futureSprintsGroupByTeam.forEach((sprints, label) => {
		groupedOptions.push({
			label,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			options: (sprints as Sprint[]).map((s) => ({
				value: s.id,
				label: s.title,
			})),
		});
	});

	const mapIdToTitle = (_: ScopeIssue, value: string): string =>
		(externalSprintsById[value] && externalSprintsById[value].title) ||
		getSprintName(value, sprints);

	let value: string | null | undefined = showOptimizations
		? getOptimizedValue(issue, issueAttributeName)
		: sprintOfIssue;

	let isClearable = true;

	if (!isDefined(value) && completedSprints.length > 0) {
		value = completedSprints[0];
		isClearable = false;
	}
	if (sprintOfIssueIsCompleted(issue)) {
		isClearable = false;
	}

	const currentSprint = sprints.find((sprint) => sprint.id === value);

	type Value = {
		value: string | null | undefined;
		label: string | null;
		isExternal?: boolean;
	};
	let sprintValue: Value | undefined;

	if (currentSprint) {
		sprintValue = {
			label: currentSprint.title,
			value: currentSprint.id,
		};
	}
	if (!isDefined(sprintValue) && isDefined(value)) {
		const externalSprintOption = externalSprintsById[value];

		if (externalSprintOption) {
			sprintValue = {
				value: externalSprintOption.id,
				label: externalSprintOption.title,
				isExternal: true,
			};
			if (externalSprintOption.state !== SPRINT_STATES.CLOSED) {
				groupedOptions.push({
					label: externalSprintLabel,
					options: [
						{
							value: externalSprintOption.id,
							label: externalSprintOption.title,
							isExternal: true,
						},
					],
				});
			}
		}
	}

	let selectValue: Value | null = null; // by default the select box value is null, so nothing is selected
	if (isDefined(sprintValue)) {
		// if a sprint value is available, then we set it as the select box value
		selectValue = sprintValue;
	} else if (!isDefined(sprintValue) && completedSprints.length > 0) {
		// if sprint value is not available but if we have completed sprints, then we have to show completed sprints placeholder text so let's set a placeholder value
		selectValue = {
			value: null,
			label: null,
		};
	}

	const formatOptionLabel = getOptionLabel({
		issue,
		issueStatuses,
		sprints: allSprints,
		showOptimizations,
	});

	const withRollup = () => {
		const rolledUpValues = R.sort(
			(a, b) => a.localeCompare(b),
			(sprintRollUpForIssue || []).map((sprintId) => {
				const externalSprint = externalSprintsById[sprintId];
				if (externalSprint) {
					return externalSprint.title;
				}
				return (
					getSprintName(sprintId, sprints) ||
					// It's false positive, "missing" props are in the type.
					formatMessage(messages.externalCompletedSprint)
				);
			}),
		);

		return (
			<SelectForColumnCell
				aria-label={formatMessage({ ...messages.sprintSelectorLabel }, { issue: issue.summary })}
				attribute="sprint"
				formatOptionLabel={formatOptionLabel}
				isClearable={isClearable}
				isDisabled={isReadOnly}
				issueId={issue.id}
				minSelectWidth={MIN_SELECT_WIDTH}
				onChange={handleSelection}
				options={groupedOptions}
				placeholder={formatMessage(messages.placeholder)}
				rolledUpValues={rolledUpValues}
				value={selectValue}
			/>
		);
	};

	return (
		<Cell
			attribute={issueAttributeName}
			issue={issue}
			valueMapper={mapIdToTitle}
			isScrolling={isScrolling}
			showOptimizations={showOptimizations}
		>
			{isDefined(message) ? (
				<Tooltip
					content={
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						<div className={styles.tooltip}>
							<h4>{message.header}</h4>
							<p>{message.content}</p>
						</div>
					}
				>
					{withRollup()}
				</Tooltip>
			) : (
				withRollup()
			)}
		</Cell>
	);
};

export default withSlots({ SelectForColumnCell: slots.CellSelectWithRollup })(SprintSelect);
